<template>
    <div id="asset-devices-information-widget-component">
        <widget-wrapper-component
            :title="$tc('asset_devices_information_widget_component.devices_information', [devices.length])"
            class="overflow-hidden position-relative"
            id-widget="asset-devices-information-widget"
        >
            <template v-slot:options>
                <menu-actions-component
                    :actions="[
                        {
                            title: $t('reset_battery'),
                            icon: 'mdi-battery-sync',
                            emit: 'openResetDeviceBatteryByDownlinkDialogValidation',
                            has_access: userHasAccess(asset_module.designation, role_all_access),
                        },
                    ]"
                    v-on:openResetDeviceBatteryByDownlinkDialogValidation="openResetDeviceBatteryByDownlinkDialogValidation"
                />
            </template>
            <template v-slot:default>
                <template v-if="devices.length > 0">
                    <v-tabs v-model="selected_device" class="mb-2" v-on:change="handleDeviceSelectUpdate">
                        <v-tab
                            v-for="(device, index) in devices"
                            :key="device.id_device"
                            :class="selected_device === index ? 'font-weight-bold' : ''"
                            :value="index"
                        >
                            {{ device.device_number }}
                        </v-tab>
                    </v-tabs>

                    <v-slide-x-transition hide-on-leave>
                        <div v-if="!refresh" class="mt-4 text-sm">
                            <div class="pa-2 d-flex flex-column gap-2">
                                <DeviceInfoRowComponent icon="mdi-tag" label="asset_devices_information_widget_component.name" :value="device.device_referentiel.name" />

                                <DeviceInfoRowComponent icon="mdi-chip" label="asset_devices_information_widget_component.device_number" :value="device.device_number" />

                                <DeviceInfoRowComponent icon="mdi-access-point" label="information_device_component.network" :value="device.network_display" />

                                <DeviceInfoRowComponent icon="mdi-cube-send" label="asset_devices_information_widget_component.source_data" :value="device.operator_type" />

                                <DeviceInfoRowComponent v-if="displayTotalStatementPerDay" icon="mdi-check-network-outline" label="information_device_component.total_statement_per_day" :value="device.total_statement_per_day" />

                                <DeviceInfoRowComponent row icon="mdi-access-point-network" label="information_device_component.date_last_emission">
                                    <chips-last-statement :date="device.date_last_emission" :fallback_text="$t('error_display.no_emission')" class="ml-1" />
                                </DeviceInfoRowComponent>

                                <DeviceInfoRowComponent icon="mdi-battery-70" label="information_device_component.battery" :value="device.battery">
                                    <span v-if="device.battery" class="font-weight-bold">{{ device.battery }} %</span>
                                    <v-icon v-else dense>mdi-battery-unknown</v-icon>
                                </DeviceInfoRowComponent>

                                <div v-if="device.is_bug_hardware">
                                    <span class="font-color-light warning--text">
                                        <v-icon class="mr-1 warning--text" dense>mdi-alert-outline</v-icon>
                                        {{ $t('information_device_component.bug_device_detected') }}
                                    </span>
                                </div>

                                <DeviceInfoRowComponent icon="mdi-compass-outline" label="information_device_component.orientation" :value="device.orientation ? displayToHumanOrientation(device.orientation) : null" />

                                <DeviceInfoRowComponent
                                    v-if="device.is_sentinel_active || device.is_threshold_low_active || device.is_threshold_high_active || device.is_integrated_device || device.is_treatment_adblue"
                                    icon="mdi-cogs"
                                    label="information_device_component.activated_options"
                                    :force_visibility="!!device.is_sentinel_active || !!device.is_threshold_low_active || !!device.is_threshold_high_active || !!device.is_integrated_device || !!device.is_treatment_adblue"
                                >
                                    <span>
                                        <br />
                                        <span v-if="device.is_sentinel_active" class="font-weight-bold ml-8">
                                            - {{ $t('information_device_component.sentinel_mode') }}<br />
                                        </span>
                                        <span v-if="device.is_threshold_low_active" class="font-weight-bold ml-8">
                                            - {{ $t('information_device_component.low_threshold') }}<br />
                                        </span>
                                        <span v-if="device.is_threshold_high_active" class="font-weight-bold ml-8">
                                            - {{ $t('information_device_component.high_threshold') }}<br />
                                        </span>
                                        <span v-if="device.is_integrated_device" class="font-weight-bold ml-8">
                                            - {{ $t('information_device_component.integrated_device') }}<br />
                                        </span>
                                        <span v-if="device.is_treatment_adblue" class="font-weight-bold ml-8">
                                            - AdBlue<br />
                                        </span>
                                    </span>
                                </DeviceInfoRowComponent>

                                <DeviceInfoRowComponent icon="mdi-calendar-check" label="information_device_component.version" :force_visibility="!!device.version_hardware || !!device.version_software">
                                    <span>
                                        <br />
                                        <div v-if="device.version_hardware">
                                            <span class="font-color-light ml-7">{{ $t('information_device_component.version_hardware') }} : </span>
                                            <span class="font-weight-bold">{{ device.version_hardware }}</span><br />
                                        </div>
                                        <div v-if="device.version_software">
                                            <span class="font-color-light ml-7">{{ $t('information_device_component.version_software') }} : </span>
                                            <span class="font-weight-bold">{{ device.version_software }}</span><br />
                                        </div>
                                    </span>
                                </DeviceInfoRowComponent>

                                <v-divider class="my-1" />

                                <DeviceInfoRowComponent icon="mdi-credit-card-settings-outline" label="information_device_component.subscribe" :force_visibility="!!(device.contract_paid_by_professional || (device.date_subscription_start && device.date_subscription_end))">
                                    <span v-if="device.contract_paid_by_professional" class="font-weight-bold">
                                        {{ $t('information_device_component.subscribe_handle_by') }}
                                        {{ device.contract.professional.distributor_name }}
                                    </span>
                                    <template v-else>
                                        <span class="font-weight-bold">
                                            {{
                                                $t('information_device_component.date_from_to', [
                                                    moment(device.date_subscription_start).format(date_format),
                                                    moment(device.date_subscription_end).format(date_format),
                                                ])
                                            }}
                                        </span>
                                        <br />
                                        <p class="font-italic font-12 ml-7 mb-1 position-relative" style="bottom: 5px">
                                            {{
                                                new Date(device.date_subscription_end) > new Date()
                                                    ? $t('information_device_component.subscribe_end_at', [moment(device.date_subscription_end).fromNow()])
                                                    : $t('information_device_component.subscribe_end_until', [moment(device.date_subscription_end).fromNow()])
                                            }}
                                        </p>
                                    </template>
                                </DeviceInfoRowComponent>

                                <DeviceInfoRowComponent v-if="displayActivationDevice || displayActivationInterface" icon="mdi-calendar-check" label="information_device_component.date_activation">
                                    <span>
                                        <br />
                                        <span v-if="displayActivationInterface">
                                            <span class="font-color-light ml-7">{{ $t('information_device_component.date_activation_interface') }} : </span>
                                            <span class="font-weight-bold">{{ displayActivationInterface }}</span><br />
                                        </span>
                                        <span v-if="displayActivationDevice">
                                            <span class="font-color-light ml-7">{{ $t('information_device_component.date_activation_device') }} : </span>
                                            <span class="font-weight-bold">{{ displayActivationDevice }}</span><br />
                                        </span>
                                    </span>
                                </DeviceInfoRowComponent>
                                <img
                                    v-if="device.path_image_device_referentiel"
                                    :src="device.path_image_device_referentiel"
                                    alt="device"
                                    class="image-device"
                                    style="pointer-events: none;"
                                />
                            </div>
                        </div>
                    </v-slide-x-transition>
                </template>

                <template v-else>
                    {{ $t('asset_devices_information_widget_component.no_device_associated') }}
                </template>
            </template>
        </widget-wrapper-component>

        <ResetDeviceBatteryByDownlinkDialogValidationComponent ref="resetDeviceBatteryByDownlinkDialogValidation" />
    </div>
</template>

<script>
import WidgetWrapperComponent from '@/components/global/wrapper/WidgetWrapperComponent.vue'
import ChipsLastStatement from '@/components/global/containment/ChipsLastStatement.vue'
import displayHuman from '@/mixin/displayHuman'
import { nextTick } from 'vue'
import MenuActionsComponent from '@/components/global/containment/MenuActionsComponent.vue'
import ResetDeviceBatteryByDownlinkDialogValidationComponent from '@/components/device/validation/ResetDeviceBatteryByDownlinkDialogValidationComponent.vue'
import DeviceInfoRowComponent from '@/components/equipment/generic/widget/DeviceInfoRowComponent.vue'

export default {
    name: 'AssetDevicesInformationWidgetComponent',
    components: {
        ResetDeviceBatteryByDownlinkDialogValidationComponent,
        MenuActionsComponent,
        ChipsLastStatement,
        WidgetWrapperComponent,
        DeviceInfoRowComponent,
    },
    mixins: [displayHuman],
    props: {
        devices: {
            type: Array,
            required: true,
        },
        asset_module: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            selected_device: 0,
            refresh: false,
        }
    },
    computed: {
        device() {
            return this.devices[this.selected_device]
        },
        displayActivationInterface() {
            return this.device.date_activation_interface
                ? this.moment(this.device.date_activation_interface).format(this.date_format)
                : null
        },
        displayActivationDevice() {
            return this.device.date_activation_device ? this.moment(this.device.date_activation_device).format(this.date_format) : null
        },
        displayTotalStatementPerDay() {
            return this.device.total_statement_per_day > 0
        },
    },
    methods: {
        handleDeviceSelectUpdate() {
            this.refresh = true
            nextTick(() => {
                this.refresh = false
            })
        },

        openResetDeviceBatteryByDownlinkDialogValidation() {
            this.$refs.resetDeviceBatteryByDownlinkDialogValidation.openResetDeviceBatteryByDownlinkDialogValidation(this.device)
        },
    },
}
</script>
